/**
 * Compact axis/tick number formatting, shared by the chart renderer.
 *
 * Simon's 2026-09-02 demo review: the monthly revenue graph rendered
 * "$1003k". Past a million, thousands stop being readable -- the reader
 * has to count digits to tell $1003k from $103k. The crossover is
 * rounding-aware: anything that would render with four digits in K
 * reads in millions instead, so "$1000k" cannot occur.
 *
 * Two decimals on the M branch on purpose. One decimal collapses
 * $1,003,000 and $1,049,000 to the same "$1.0M", and a monthly revenue
 * axis for a hauler this size spends most of its range inside the first
 * few hundred thousand of that million, so the second decimal is the
 * only thing separating adjacent ticks.
 *
 * The sign leads the glyph ("-$1.50M", matching Intl's currency
 * convention) rather than sitting between them ("$-1.5M", which is what
 * the previous inline implementation produced for the negative
 * adjustment months on the billing charts).
 *
 * Pure and dependency-free so node:test imports it directly with no
 * TypeScript build step (the grainDefaults.mjs / fallbackWindow.mjs
 * precedent). The companion compactNumber.d.mts gives TS callers types.
 */

/** The K -> M crossover. A value at or above this reads in millions. */
export const MILLION = 1_000_000;
/** The unit -> K crossover. A value at or above this reads in thousands. */
export const THOUSAND = 1_000;

/**
 * Compact a dollar figure for a chart axis or tick.
 *
 * @param {number} value dollars
 * @returns {string} e.g. "$1.00M", "$403k", "$87", "-$1.50M"
 */
export function compactCurrency(value) {
  return `${signOf(value)}$${compactMagnitude(value)}`;
}

/**
 * Compact a count for a chart axis or tick. Same crossovers, no glyph.
 *
 * @param {number} value a count
 * @returns {string} e.g. "1.00M", "403k", "87", "-1.50M"
 */
export function compactInteger(value) {
  return `${signOf(value)}${compactMagnitude(value)}`;
}

/** "-" for a negative that survives rounding, "" otherwise. */
function signOf(value) {
  if (!Number.isFinite(value) || value >= 0) return "";
  // -0.4 rounds to 0; do not render "-$0".
  return Math.abs(value) >= 0.5 ? "-" : "";
}

/**
 * Unsigned magnitude with its unit suffix. Callers prepend the sign so
 * the currency glyph never lands between the minus and the digits.
 */
function compactMagnitude(value) {
  if (!Number.isFinite(value)) return "0";
  const abs = Math.abs(value);
  if (abs >= MILLION) return `${(abs / MILLION).toFixed(2)}M`;
  if (abs >= THOUSAND) {
    // AJ, 2026-09-02: "if it's a million, it should say 1m".
    //
    // The crossover is ROUNDING-AWARE, not a raw compare against
    // 1,000,000. An earlier version kept 999,999 in K on the reasoning
    // that the unit should not jump on a display artifact, which meant
    // a tick rendered "$1000k". Four digits in K is the exact thing
    // that made "$1003k" unreadable in the first place: the reader has
    // to count digits to tell it from "$100k".
    //
    // So promote whenever the K rendering would reach four digits. In
    // practice that moves the boundary to 999,500, and no tick can ever
    // render as "$1000k" again.
    const thousands = Math.round(abs / THOUSAND);
    if (thousands >= 1000) return `${(abs / MILLION).toFixed(2)}M`;
    return `${thousands}k`;
  }
  return `${Math.round(abs)}`;
}
